import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ReduxStates } from '../../../redux/reduxTypes';
import { MultiMetricData, MultiMetricOptionsProps } from '../../../types';
import MultiMetricDropdownOption from './MultiMetricDropdownOption';
import { ReactComponent as DropDown } from '../../../assets/icons/graph-table-dropdown-icon.svg';

export interface DropDownProps {
  data: MultiMetricData[] | null;
  onUpdate: (value: MultiMetricOptionsProps[]) => void;
  scrollable?: boolean;
}

const MultiMetricDropdown: React.FC<DropDownProps> = ({ data, scrollable, onUpdate }: DropDownProps) => {
  const [options, setOptions] = useState<MultiMetricOptionsProps[] | null>(null);
  const [allVisible, setAllVisible] = useState<boolean>(true);
  const [viewAll, setViewAll] = useState<boolean>(true);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const [visibleOptions, setVisibleOptions] = useState<MultiMetricOptionsProps[] | null>(null);

  const ref = React.useRef(null);

  const handleClickOutside = (event: Event) => {
    if (ref.current && !(ref.current as any).contains(event.target)) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (data) {
      const dataMetrics = [...data];
      const optionsFromData = dataMetrics.map((metric) => {
        return {
          id: metric.dimension,
          label: metric.label,
          visible: true
        };
      });
      setOptions(optionsFromData);
    }
  }, [data]);

  useEffect(() => {
    if (options) {
      onUpdate(options);
      const hasAllVisibility = options.every((option) => option.visible);
      setAllVisible(hasAllVisibility);

      const visibleOptions = options.filter((option) => option.visible);
      setVisibleOptions(visibleOptions);
    }
  }, [options]);

  useEffect(() => {
    if (options) {
      const newArr = [...options]; // copying the old datas array
      newArr.map((metric) => (metric.visible = viewAll));
      setOptions(newArr);
    }
  }, [viewAll]);

  const toggleVisibility = (visible: boolean, option: MultiMetricOptionsProps) => {
    if (options) {
      const newArr = [...options]; // copying the old datas array
      const item = newArr?.filter((o: MultiMetricOptionsProps) => o.id === option.id);
      item[0].visible = visible;
      setOptions(newArr);
    }
  };

  return (
    <div id="multimetric_dropdown" ref={ref}>
      <p className="text-xs leading-4 text-[#2b2b2b] tracking-[0.1px] uppercase mx-0  mt-[20px] mb-[8px] font-nunitoBold">
        {copyData.multimetric_metric}:
      </p>
      {options && options.length > 0 && (
        <div className="relative z-20 w-max">
          <div
            className="relative cursor-pointer flex items-center justify-between bg-white box-border rounded-[4px] h-[40px] shadow-[0px 1px 2px rgba(0, 0, 0, 0.1)] w-[255px] px-[13px] border-[1px] border-[rgba(0, 59, 88, 0.21)]"
            onClick={() => setShowOptions(!showOptions)}
          >
            {visibleOptions && visibleOptions.length > 0 ? (
              <p className="text-[14px] font-nunitoLight">
                {visibleOptions[0].label}{' '}
                <span className="text-[#B9B9B9]">
                  {visibleOptions.length > 1 ? '(+ ' + (visibleOptions.length - 1) + ' others)' : ''}
                </span>
              </p>
            ) : (
              <p className="text-[14px] font-nunitoLight">{copyData.multimetric_none}</p>
            )}
            <DropDown />
          </div>
          {showOptions && (
            <div
              className={`absolute left-0 filter drop-shadow-dropdownFilter border border-[#e5e5ea] bg-white rounded-[2px] w-full p-[3px]  ${
                scrollable ? 'max-h-[400px] overflow-y-auto custom-scrollbar' : ''
              }`}
            >
              <MultiMetricDropdownOption
                id={'all'}
                isVisible={allVisible}
                label={copyData?.multimetric_all}
                toggleVisibility={() => {
                  if (!allVisible && viewAll) {
                    options.forEach((option: MultiMetricOptionsProps) => toggleVisibility(true, option));
                  } else {
                    setViewAll(!viewAll);
                  }
                }}
              />
              {options.map((option: MultiMetricOptionsProps) => {
                return (
                  <MultiMetricDropdownOption
                    key={option.id}
                    id={option.id}
                    label={option.label}
                    isVisible={option.visible}
                    toggleVisibility={(value) => toggleVisibility(value, option)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiMetricDropdown;
